import { Button, cn, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { apiFetch } from '../../lib/apiFetch.js';
import { downloadCredentialed } from '../../lib/download.js';
import { isRichTextEmpty, sanitizeRichTextHtml } from '../../lib/richTextHtml.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { useJobAccess } from '../jobs/useJobAccess.js';
import { EstimateLineItemsTable } from './components/EstimateLineItemsTable.js';
import { EstimateStatGrid } from './components/EstimateStatGrid.js';
import { PublicApprovalLink } from './components/PublicApprovalLink.js';
import type { EstimateDetail, EstimateFieldReport } from './contractsMirror.js';
import { estimatePdfUrl } from './exportLinks.js';
import { estimateQueryKey } from './useEstimateQuery.js';

// Sent and rejected are editable too: a customer asking for a change is revised in place rather
// than re-keyed as a new estimate. The api worker is the authority (409 not_editable otherwise).
const EDITABLE_STATUSES = new Set(['draft', 'ready', 'sent', 'rejected']);
const SENDABLE_STATUSES = new Set(['draft', 'ready', 'sent']);
type PendingAction = null | 'send' | 'convert' | 'archive' | 'restore';

/**
 * Estimate-detail screen (legacy `EstimateDetailPage`) on the locked brand,
 * uplifted to the P22 redesign grammar (D-04 re-skin — functionality UNCHANGED).
 * Header + actions (Download Proposal PDF / Back), signed banner, and the detail
 * sections grouped into accessible **sub-tabs** (UI-SPEC §3 "Detail screens use
 * sub-tabs"): Overview (stat grid + scope + customer), Line Items, and Approval
 * (the customer-approval delivery card + the approval-notes / rejection-reason /
 * converted-job cards). The tablist is a proper `role="tablist"` set, arrow-key
 * navigable, active tab underlined with the accent (A11Y-01).
 *
 * P21 CONTRACTS PRESERVED (no change this re-skin):
 * - **Public approval/signature STAYS SSR**: `PublicApprovalLink` is a copy/open
 *   link-OUT to the legacy SSR public estimate page — the customer sign+approve
 *   canvas is NEVER re-implemented in this authenticated SPA (D-02 / A2 / Pitfall 2).
 * - **Approve → auto-job (no invoice)**: the "Converted Job" card links to that
 *   job's detail page (`/jobs/$id`); the conversion happens on the SSR approve flow
 *   or the Convert action here. Convert lands on the new job's detail page too.
 * - **Exports via api-host GET**: "Download Proposal PDF" links to `estimatePdfUrl`
 *   (engine reused, D-06 — never a client renderer). No hook/endpoint change.
 */

export interface EstimateDetailViewProps {
  estimate: EstimateDetail;
  /** The crew's report from the visit this estimate came from, when it came from one. */
  fieldReport?: EstimateFieldReport | null;
}

type DetailTab = 'overview' | 'lineItems' | 'approval';

export function EstimateDetailView({ estimate, fieldReport = null }: EstimateDetailViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Archival sets archived_at and preserves status (it never becomes 'archived'), so key the
  // archived flag off archivedAt — matching how InvoiceDetailView derives archived (21-13 D2).
  // This makes the already-present Restore button render for archived estimates.
  const archived = Boolean(estimate.archivedAt);
  const lineItems = estimate.lineItems ?? [];

  // State-gated actions (mirror the SSR detail): each button reflects the
  // estimate's current server status. Every mutation goes through apiFetch (the
  // double-submit CSRF token is baked in) and never sends a tenant id (SEC-11).
  const canEdit = EDITABLE_STATUSES.has(estimate.status);
  const canSend = SENDABLE_STATUSES.has(estimate.status) && Boolean(estimate.customerEmail);
  // POST /estimates/:id/convert wants Manager+ and jobs.create.
  const { canCreateFromRecord } = useJobAccess();
  const canConvert = !estimate.converted_job_id && !archived && canCreateFromRecord;

  const [pending, setPending] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Exclude<PendingAction, 'send' | null>>('convert');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const invalidateDetail = () =>
    queryClient.invalidateQueries({ queryKey: estimateQueryKey(estimate.id) });

  // POST an action endpoint; returns the parsed envelope or null on failure
  // (with actionError already surfaced).
  const postAction = async (
    action: Exclude<PendingAction, null>,
  ): Promise<{ ok?: boolean; error?: string; jobId?: number } | null> => {
    setActionError(null);
    setPending(action);
    try {
      const response = await apiFetch(`/api/v1/estimates/${estimate.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        jobId?: number;
      } | null;
      if (!response.ok || !result || result.ok === false) {
        setActionError(result?.error ?? t('estimates:actions.genericError'));
        return null;
      }
      return result;
    } catch {
      setActionError(t('estimates:actions.genericError'));
      return null;
    } finally {
      setPending(null);
    }
  };

  const onSend = async () => {
    const result = await postAction('send');
    if (result) await invalidateDetail(); // the public link now appears on the Approval tab
  };

  const runConfirmed = async () => {
    const action = confirm;
    const result = await postAction(action);
    if (!result) return; // keep the dialog open with the surfaced error
    setConfirmOpen(false);
    if (action === 'convert') {
      // Land on the job that was just made, not the jobs list. The list is only a fallback for a
      // response that does not name the job.
      if (typeof result.jobId === 'number' && result.jobId > 0) {
        void navigate({ to: '/jobs/$id', params: { id: String(result.jobId) } });
      } else {
        void navigate({ to: '/jobs' });
      }
    } else if (action === 'archive') {
      void navigate({ to: '/estimates' });
    } else {
      await invalidateDetail(); // restore → stay, refetch active status
    }
  };

  const openConfirm = (action: Exclude<PendingAction, 'send' | null>) => {
    setActionError(null);
    setConfirm(action);
    setConfirmOpen(true);
  };

  const confirmCopy = {
    convert: {
      heading: t('estimates:convertDialog.heading'),
      body: t('estimates:convertDialog.body'),
      confirmLabel: t('estimates:convertDialog.confirm'),
    },
    archive: {
      heading: t('estimates:archiveDialog.heading'),
      body: t('estimates:archiveDialog.body'),
      confirmLabel: t('estimates:archiveDialog.confirm'),
    },
    restore: {
      heading: t('estimates:restoreDialog.heading'),
      body: t('estimates:restoreDialog.body'),
      confirmLabel: t('estimates:restoreDialog.confirm'),
    },
  }[confirm];

  // Base cost = the per-line base-cost × quantity rolled up (legacy `totalBaseCost`);
  // markup value = subtotal − base cost (legacy `grossMarkupValue`). Computed from
  // the coarse payload — no client re-aggregation of the list, just the detail's
  // own already-fetched rows.
  const baseCost = lineItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.baseCost || 0),
    0,
  );
  const subtotal = Number(estimate.subtotal || 0);
  const markupValue = subtotal - baseCost;
  // Empty-checked through the same sanitizer, so a value that is only markup ("<p></p>", which is
  // what an editor that has been cleared stores) reads as absent rather than rendering a blank box.
  const scopeHtml = isRichTextEmpty(estimate.scopeOfWork)
    ? ''
    : sanitizeRichTextHtml(estimate.scopeOfWork);

  const tabs: { id: DetailTab; label: string }[] = [
    { id: 'overview', label: t('estimates:detail.summaryHeading') },
    { id: 'lineItems', label: t('estimates:detail.lineItemsHeading') },
    { id: 'approval', label: t('estimates:approval.heading') },
  ];
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tablistId = useId();

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    setActiveTab(tabs[next].id);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <Link
          to="/estimates"
          className="mb-3 inline-flex min-h-11 items-center gap-1 rounded-md pr-3 font-medium text-muted-foreground text-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t('estimates:actions.backToEstimates')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-semibold text-3xl text-foreground">
              {estimate.estimateNumber || `#${estimate.id}`}
            </h1>
            {estimate.customerName ? (
              <p className="mt-1 text-muted-foreground text-sm">{estimate.customerName}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit ? (
              <Button asChild>
                <Link to="/estimates/$id/edit" params={{ id: String(estimate.id) }}>
                  {t('estimates:actions.editShort')}
                </Link>
              </Button>
            ) : null}
            {canSend ? (
              <Button variant="secondary" disabled={pending !== null} onClick={() => void onSend()}>
                {pending === 'send' ? t('estimates:actions.working') : t('estimates:actions.send')}
              </Button>
            ) : null}
            {canConvert ? (
              <Button
                variant="secondary"
                disabled={pending !== null}
                onClick={() => openConfirm('convert')}
              >
                {t('estimates:actions.convertToJob')}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() =>
                void downloadCredentialed(
                  estimatePdfUrl(estimate.id),
                  `estimate-${estimate.estimateNumber || estimate.id}.pdf`,
                ).catch((err) => console.error('estimate pdf download failed', err))
              }
            >
              {t('estimates:actions.downloadPdf')}
            </Button>
            {archived ? (
              <Button
                variant="secondary"
                disabled={pending !== null}
                onClick={() => openConfirm('restore')}
              >
                {t('estimates:actions.restore')}
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                disabled={pending !== null}
                onClick={() => openConfirm('archive')}
              >
                {t('estimates:actions.archive')}
              </Button>
            )}
          </div>
        </div>
      </header>

      {actionError && !confirmOpen ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {actionError}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        heading={confirmCopy.heading}
        body={confirmCopy.body}
        confirmLabel={confirmCopy.confirmLabel}
        cancelLabel={t('estimates:actions.cancel')}
        confirmPending={pending !== null}
        errorMessage={actionError}
        onConfirm={() => void runConfirmed()}
        onCancel={() => {
          setConfirmOpen(false);
          setActionError(null);
        }}
      />

      {estimate.signatureName ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-(--chart-3)/30 bg-(--chart-3)/10 p-4 text-success text-sm">
          <span className="inline-flex items-center gap-2 font-medium">
            <CheckCircle2 className="size-4" aria-hidden="true" />
            {t('estimates:signed.badge')}
          </span>
          <span>{t('estimates:signed.by', { name: estimate.signatureName })}</span>
        </div>
      ) : null}

      {/* Detail sub-tabs (UI-SPEC §3): arrow-key navigable, active = accent underline. */}
      <div
        role="tablist"
        aria-label={estimate.estimateNumber || `#${estimate.id}`}
        className="mb-4 flex flex-nowrap gap-1 overflow-x-auto border-border border-b [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] pe-8"
      >
        {tabs.map((tab, index) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`${tablistId}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${tablistId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              className={cn(
                'inline-flex h-11 shrink-0 items-center border-b-2 px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                selected
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`${tablistId}-panel-${activeTab}`}
        aria-labelledby={`${tablistId}-tab-${activeTab}`}
      >
        {activeTab === 'overview' ? (
          <>
            <EstimateStatGrid
              status={estimate.status}
              baseCost={baseCost}
              subtotal={subtotal}
              markupValue={markupValue}
              archived={archived}
              fmt={fmt}
            />
            {/* What the crew found on site, shown ABOVE the scope so it is read before the work is
                priced. It is reference material and never part of the document: plain text through
                React's own escaping, never the rich-text renderer the scope below uses, because
                this field is written from a page whose only credential is a shared crew link. */}
            {fieldReport ? (
              <section className="mb-4 rounded-md border border-border bg-card p-6">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-semibold text-foreground text-xl">
                    {t('estimates:detail.fieldReportHeading')}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {t('estimates:detail.fieldReportBy', {
                      crew: fieldReport.crewName,
                      date: formatUiDate(fieldReport.visitedOn, locale, { dateStyle: 'medium' }),
                    })}
                  </p>
                </div>
                <p className="whitespace-pre-line text-foreground text-sm">{fieldReport.notes}</p>
                <p className="mt-2 text-muted-foreground text-xs">
                  {t('estimates:detail.fieldReportNote')}
                </p>
              </section>
            ) : null}

            <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
                <h2 className="mb-3 font-semibold text-foreground text-xl">
                  {t('estimates:detail.scopeHeading')}
                </h2>
                {/* `scope_of_work` is the richest HTML field the product has — one production
                    tenant uses it as a whole document editor, ~6,000 characters with its own price
                    list and sign-off. Rendered rather than flattened so this pane shows what the
                    customer will actually receive, through the allowlist that mirrors the api
                    worker's sanitizeRichHtml (lib/richTextHtml.ts). */}
                {scopeHtml ? (
                  <div
                    className="rich-text text-foreground text-sm"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized at assignment
                    dangerouslySetInnerHTML={{ __html: scopeHtml }}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">{t('estimates:detail.noScope')}</p>
                )}
              </div>
              <div className="rounded-md border border-border bg-card p-6">
                <h2 className="mb-3 font-semibold text-foreground text-xl">
                  {t('estimates:detail.customerHeading')}
                </h2>
                <p className="font-medium text-foreground text-sm">
                  {estimate.customerName || t('estimates:table.noCustomer')}
                </p>
                {estimate.expiresAt ? (
                  <dl className="mt-4 grid gap-1 border-border border-t pt-4 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">{t('estimates:detail.expiresAt')}</dt>
                      <dd className="font-mono text-foreground">
                        {formatUiDate(estimate.expiresAt, locale, { dateStyle: 'medium' })}
                      </dd>
                    </div>
                  </dl>
                ) : null}
              </div>
            </section>
          </>
        ) : null}

        {activeTab === 'lineItems' ? (
          <section className="rounded-md border border-border bg-card p-6">
            <h2 className="mb-3 font-semibold text-foreground text-xl">
              {t('estimates:detail.lineItemsHeading')}
            </h2>
            <EstimateLineItemsTable rows={lineItems} fmt={fmt} />
          </section>
        ) : null}

        {activeTab === 'approval' ? (
          <div className="grid gap-4">
            <section className="rounded-md border border-border bg-card p-6">
              <h2 className="font-semibold text-foreground text-xl">
                {t('estimates:approval.heading')}
              </h2>
              <p className="mt-1 text-muted-foreground text-sm">
                {t('estimates:approval.subtitle')}
              </p>
              <div className="mt-4">
                {/* Public approval/signature STAYS SSR — this is a copy/open link-OUT only. */}
                <PublicApprovalLink publicToken={estimate.public_token} />
              </div>
            </section>

            {estimate.approvalNotes || estimate.rejectionReason || estimate.converted_job_id ? (
              <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {estimate.approvalNotes ? (
                  <div className="rounded-md border border-border bg-card p-6">
                    <h2 className="mb-2 font-semibold text-foreground text-xl">
                      {t('estimates:detail.approvalNotesHeading')}
                    </h2>
                    <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                      {estimate.approvalNotes}
                    </p>
                  </div>
                ) : null}
                {estimate.rejectionReason ? (
                  <div className="rounded-md border border-destructive/30 bg-card p-6">
                    <h2 className="mb-2 font-semibold text-destructive text-xl">
                      {t('estimates:detail.rejectionReasonHeading')}
                    </h2>
                    <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                      {estimate.rejectionReason}
                    </p>
                  </div>
                ) : null}
                {estimate.converted_job_id ? (
                  <div className="rounded-md border border-(--chart-3)/30 bg-card p-6">
                    <h2 className="mb-2 font-semibold text-foreground text-xl">
                      {t('estimates:detail.convertedJobHeading')}
                    </h2>
                    {/* D-02: approval auto-converts the estimate into a JOB (no invoice).
                        The card opens that job, not the jobs list. */}
                    <Button asChild variant="secondary">
                      <Link to="/jobs/$id" params={{ id: String(estimate.converted_job_id) }}>
                        {estimate.convertedJobName ||
                          t('estimates:detail.convertedJobFallback', {
                            id: estimate.converted_job_id,
                          })}
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
